// Sound Effects Utility - Subtle UI sounds with full error protection

let audioContext = null;

const getAudioContext = () => {
  if (audioContext) return audioContext;
  try {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    audioContext = null;
  }
  return audioContext;
};

const safePlay = (fn) => {
  try { fn(); } catch {}
};

const createClickSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination);
  o.frequency.setValueAtTime(800, ctx.currentTime);
  o.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.05);
  g.gain.setValueAtTime(0.1, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
  o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.05);
});

const createHoverSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination);
  o.type = 'sine';
  o.frequency.setValueAtTime(1200, ctx.currentTime);
  g.gain.setValueAtTime(0.03, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.02);
  o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.02);
});

const createSuccessSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const playTone = (freq, startTime, duration) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, startTime);
    g.gain.setValueAtTime(0.12, startTime);
    g.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    o.start(startTime); o.stop(startTime + duration);
  };
  const now = ctx.currentTime;
  playTone(523.25, now, 0.1);
  playTone(659.25, now + 0.1, 0.15);
});

const createErrorSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination);
  o.type = 'sine';
  o.frequency.setValueAtTime(300, ctx.currentTime);
  o.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.15);
  g.gain.setValueAtTime(0.1, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
  o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.15);
});

const createNotificationSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const playTone = (freq, startTime, duration) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, startTime);
    g.gain.setValueAtTime(0.08, startTime);
    g.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    o.start(startTime); o.stop(startTime + duration);
  };
  const now = ctx.currentTime;
  playTone(880, now, 0.08);
  playTone(1108.73, now + 0.08, 0.12);
});

const createSwooshSound = () => safePlay(() => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const bufferSize = ctx.sampleRate * 0.1;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const source = ctx.createBufferSource();
  const g = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  source.buffer = buffer;
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1000, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(3000, ctx.currentTime + 0.1);
  source.connect(filter); filter.connect(g); g.connect(ctx.destination);
  g.gain.setValueAtTime(0.05, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
  source.start();
});

const getSoundEnabled = () => {
  try { const s = localStorage.getItem('soundEffectsEnabled'); return s === null ? true : s === 'true'; } catch { return true; }
};

const setSoundEnabled = (enabled) => {
  try { localStorage.setItem('soundEffectsEnabled', enabled.toString()); } catch {}
};

const resumeAudioContext = () => {
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') ctx.resume();
  } catch {}
};

export const sounds = {
  click: () => getSoundEnabled() && createClickSound(),
  hover: () => getSoundEnabled() && createHoverSound(),
  success: () => getSoundEnabled() && createSuccessSound(),
  error: () => getSoundEnabled() && createErrorSound(),
  notification: () => getSoundEnabled() && createNotificationSound(),
  swoosh: () => getSoundEnabled() && createSwooshSound(),
  isEnabled: getSoundEnabled,
  setEnabled: setSoundEnabled,
  resume: resumeAudioContext
};

export default sounds;
